/* ── Quick View mode ───────────────────────────────────── */
if (!window._pcqvQvDelegated) {
  window._pcqvQvDelegated = true;
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.pcqv[data-mode="quick_view"] .pcqv__btn');
    if (!btn) return;
    const card = btn.closest('product-card');
    const trigger =
      card && (
        card.querySelector('.quick-add__button--choose') ||
        card.querySelector('.quick-add__button--add')
      );
    if (trigger) trigger.click();
  });
}

/* ── Add to Cart mode ──────────────────────────────────── */
if (!window._pcqvAtcManager) {
  window._pcqvAtcManager = new (class {
    constructor() {
      this._cart = [];
      this._sync().then(() => this._bind());

      /* Keep the steppers in step with cart changes made elsewhere (cart drawer, free gift, recommendations) */
      document.addEventListener('cart:update', (e) => {
        if (e.detail?.data?.source === 'pcqv') return;
        this._sync();
      });
    }

    /* Fetch cart and update all ATC blocks on the page */
    async _sync() {
      try {
        const res = await fetch('/cart.js');
        this._cartData = await res.json();
        this._cart = this._cartData.items;
        document.querySelectorAll('.pcqv[data-mode="add_to_cart"]').forEach(block => {
          const id = parseInt(block.dataset.variantId);
          const item = this._cart.find(i => i.variant_id === id);
          this._render(block, item ? item.quantity : 0);
        });
      } catch (_) {}
    }

    /* Ask for the cart sections in the same request, so the drawer morphs from this response instead of
       fetching itself separately (a late separate fetch can overwrite newer cart changes with stale line numbers) */
    _sectionsParams() {
      const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))];
      return ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
    }

    /* Notify the theme's cart components to re-render */
    _notifyCart(sections) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: this._cartData || {}, sourceId: '', data: { source: 'pcqv', sections, itemCount: (this._cartData || {}).item_count ?? 0 } }
      }));
    }

    _render(block, qty) {
      const btn   = block.querySelector('.pcqv__btn');
      const stepper = block.querySelector('.pcqv__qty');
      const input = block.querySelector('.pcqv__qty-input');
      if (!btn || !stepper || !input) return;
      btn.hidden     = qty > 0;
      stepper.hidden = qty <= 0;
      input.value    = qty;
    }

    _bind() {
      document.addEventListener('click', async (e) => {
        /* ATC button */
        const atcBtn = e.target.closest('.pcqv[data-mode="add_to_cart"] .pcqv__btn');
        if (atcBtn) {
          const block = atcBtn.closest('.pcqv');
          await this._setQty(block, parseInt(block.dataset.variantId), 1);
          return;
        }

        /* +/- stepper buttons */
        const stepBtn = e.target.closest('.pcqv[data-mode="add_to_cart"] .pcqv__qty-btn');
        if (stepBtn) {
          const block = stepBtn.closest('.pcqv');
          const input = block.querySelector('.pcqv__qty-input');
          const current = parseInt(input.value) || 0;
          const direction = stepBtn.dataset.action === 'increase' ? 'up' : 'down';
          const next = stepBtn.dataset.action === 'increase'
            ? current + 1
            : Math.max(0, current - 1);
          stepBtn.dataset.spinning = '';
          await this._setQty(block, parseInt(block.dataset.variantId), next, direction);
          delete stepBtn.dataset.spinning;
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        const input = e.target.closest('.pcqv[data-mode="add_to_cart"] .pcqv__qty-input');
        if (!input) return;
        e.preventDefault();
        input.blur();
      });

      document.addEventListener('focusout', async (e) => {
        const input = e.target.closest('.pcqv[data-mode="add_to_cart"] .pcqv__qty-input');
        if (!input) return;
        /* Skip if focus moved to a +/- button — that click handler will commit */
        if (e.relatedTarget?.closest('.pcqv__qty-btn')) return;
        await this._commitInput(input);
      });
    }

    async _commitInput(input) {
      const block = input.closest('.pcqv');
      const variantId = parseInt(block.dataset.variantId);
      const newQty = Math.max(0, parseInt(input.value) || 0);
      const current = this._cart.find(i => i.variant_id === variantId)?.quantity || 0;
      if (newQty === current) { input.value = current; return; }
      const spinBtns = block.querySelectorAll('.pcqv__qty-btn');
      spinBtns.forEach(b => { b.dataset.spinning = ''; });
      await this._setQty(block, variantId, newQty, newQty > current ? 'up' : 'down');
      spinBtns.forEach(b => { delete b.dataset.spinning; });
    }

    async _setQty(block, variantId, quantity, direction = null) {
      const isNewAdd = !this._cart.find(i => i.variant_id === variantId) && quantity > 0;
      block.classList.add('pcqv--loading');
      const btn = block.querySelector('.pcqv__btn');
      if (btn) btn.dataset.loading = '';
      try {
        const existing = this._cart.find(i => i.variant_id === variantId);
        let res = null;
        if (!existing && quantity > 0) {
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity,
              properties: block.dataset.preorder ? { 'Pre-order': block.dataset.preorder } : undefined,
              ...this._sectionsParams()
            })
          });
        } else if (existing) {
          res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: existing.key, quantity, ...this._sectionsParams() })
          });
        }
        const sections = res && res.ok ? (await res.json()).sections : undefined;
        await this._sync();
        this._notifyCart(sections);
      } catch (_) {
        await this._sync();
      } finally {
        block.classList.remove('pcqv--loading');
        if (btn) delete btn.dataset.loading;
      }
      if (!isNewAdd && quantity > 0 && direction) {
        const input = block.querySelector('.pcqv__qty-input');
        input.removeAttribute('data-slide');
        void input.offsetWidth; // force reflow to restart animation
        input.setAttribute('data-slide', direction);
        input.addEventListener('animationend', () => input.removeAttribute('data-slide'), { once: true });
      }
      if (isNewAdd) {
        const btn = block.querySelector('.pcqv__btn');
        const stepper = block.querySelector('.pcqv__qty');
        btn.hidden = false;
        if (stepper) stepper.hidden = true;
        btn.dataset.added = 'true';
        await new Promise(r => setTimeout(r, 1200));
        delete btn.dataset.added;
        const item = this._cart.find(i => i.variant_id === variantId);
        this._render(block, item ? item.quantity : 0);
      }
    }
  })();
}
